import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { STOP_OPERATOR } from 'lib/api/mutations';
import { getMachine } from 'lib/selectors/getMachine';
import { getOperatorIsLoggingOut } from 'lib/selectors/getOperatorIsLoggingOut';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import {
  actionSetRecentOperatorRuns,
  actionSetOperatorIsLoggingOut,
  actionOpenClockOutModal,
} from 'lib/actions';

const useOperatorLogout = (onComplete = () => {}) => {
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const isLoggingOut = useSelector(getOperatorIsLoggingOut);
  const currentOperator = useSelector(getCurrentOperator);
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);

  const [stopOperator, { loading }] = useMutation(STOP_OPERATOR, {
    fetchPolicy: 'no-cache',
    onCompleted: async ({ stopOperatorResponse }) => {
      if (stopOperatorResponse) {
        const {
          machine: localMachine,
          operator,
          ...operatorRun
        } = stopOperatorResponse;
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: localMachine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));

        // Open clockOut modal if embedded labor flow is enabled and either
        // clock out flow or shift handover is enabled
        if (
          (clockOutFlowEnabled || shiftHandoverEnabled) &&
          isEmbeddedLaborFlowEnabled
        ) {
          // Use current operator from store (they're the one signing out)
          dispatch(actionOpenClockOutModal(currentOperator?.erpId));
        }

        onComplete();
      }
    },
  });

  useEffect(() => {
    dispatch(actionSetOperatorIsLoggingOut(loading));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return async () => {
    if (isLoggingOut || loading) {
      return;
    }
    await stopOperator({
      variables: {
        input: {
          machineId: machine.id,
        },
      },
    });
  };
};

export default useOperatorLogout;
